/**
 * Container Data Review Actions.
 *
 * Two UI entry points into the EveCare data-review flow:
 *
 * 1. <FieldVerifyButton>: small icon button placed next to a VIP field on the
 *    container detail page. On click, opens EveCare (Ask Eve panel) and
 *    auto-submits a prompt asking for the provenance of that field. EveCare
 *    will call `get_field_provenance` and render a <ProvenancePill> inline.
 *
 * 2. <ForensicsQuickActionsMenu>: dropdown button that offers preset
 *    forensics questions ("Verifica ETA", "Verifica porto scarico", ...) plus
 *    a custom free-text option. On selection, opens EveCare and submits the
 *    forensics prompt. EveCare will call `analyze_container_history` and
 *    render a <ForensicsReportCard> inline.
 *
 * Both components use the existing `useChatAI` context — no API client work.
 */

import { useState } from "react";
import { InfoCircle, SearchLg, ChevronDown } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { useChatAI } from "./chat-ai-context";

interface FieldVerifyButtonProps {
    fieldLabel: string;
    fieldName: string;
    currentValue?: string | number | null;
    containerNumber?: string | null;
    ariaLabel?: string;
}

/**
 * Small icon button placed next to a field value.
 * Opens EveCare with a prefilled "da dove viene questo dato?" prompt.
 */
export const FieldVerifyButton = ({
    fieldLabel,
    fieldName,
    currentValue,
    containerNumber,
    ariaLabel,
}: FieldVerifyButtonProps) => {
    const { openAskEvePanel, submitPrompt } = useChatAI();

    const handleClick = () => {
        const valueStr =
            currentValue === null || currentValue === undefined || currentValue === ""
                ? "(vuoto)"
                : String(currentValue);
        const containerHint = containerNumber ? ` del container ${containerNumber}` : "";
        const prompt =
            `Da dove viene il dato "${fieldLabel}" (campo ${fieldName}, valore attuale: ${valueStr})${containerHint}? ` +
            `Usa get_field_provenance per recuperare la fonte e mostrami il risultato.`;

        openAskEvePanel();
        submitPrompt(prompt);
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className="ml-1 inline-flex items-center justify-center rounded-full p-1 text-tertiary hover:text-brand hover:bg-secondary transition-colors"
            aria-label={ariaLabel ?? `Verifica fonte del dato ${fieldLabel}`}
            title={`Verifica fonte del dato "${fieldLabel}"`}
        >
            <InfoCircle className="size-3.5" />
        </button>
    );
};

interface ForensicsQuickActionsMenuProps {
    containerNumber?: string | null;
}

type ForensicsPreset = {
    id: string;
    label: string;
    question: string;
};

const PRESETS: ForensicsPreset[] = [
    {
        id: "eta",
        label: "Verifica ETA",
        question:
            "Controlla se l'ETA di questo container è corretto. Analizza tutte le fonti (email, documenti, tracking carrier) e dimmi se ci sono discrepanze o aggiornamenti recenti che non sono stati recepiti.",
    },
    {
        id: "pod",
        label: "Verifica porto di scarico",
        question:
            "Controlla se il porto di scarico di questo container è corretto. Verifica coerenza tra documenti, email del carrier e tracking JSONCargo.",
    },
    {
        id: "carrier",
        label: "Verifica carrier e vessel",
        question:
            "Controlla se carrier, vessel e voyage di questo container sono coerenti tra le diverse fonti (documenti, email, tracking).",
    },
    {
        id: "weight",
        label: "Verifica peso e cargo",
        question:
            "Controlla se peso e dettagli del cargo di questo container sono coerenti tra i documenti. Segnala eventuali discrepanze tra B/L, Invoice e Packing List.",
    },
];

/**
 * Dropdown that exposes preset forensics questions and a custom option.
 * Selecting a preset opens EveCare and auto-submits the prompt, which causes
 * `analyze_container_history` to be invoked.
 */
export const ForensicsQuickActionsMenu = ({
    containerNumber,
}: ForensicsQuickActionsMenuProps) => {
    const { openAskEvePanel, submitPrompt } = useChatAI();
    const [open, setOpen] = useState(false);
    const [customOpen, setCustomOpen] = useState(false);
    const [customText, setCustomText] = useState("");

    const runPrompt = (question: string) => {
        const containerHint = containerNumber ? ` (container ${containerNumber})` : "";
        const prompt =
            `${question}${containerHint}\n\n` +
            `Usa analyze_container_history per un'analisi approfondita e mostrami il report.`;
        openAskEvePanel();
        submitPrompt(prompt);
        setOpen(false);
    };

    const handleCustom = () => {
        if (customText.trim().length < 5) return;
        runPrompt(customText.trim());
        setCustomText("");
        setCustomOpen(false);
    };

    return (
        <div className="relative inline-block">
            <Button
                size="sm"
                color="secondary"
                iconLeading={SearchLg}
                onClick={() => {
                    setOpen((v) => !v);
                    setCustomOpen(false);
                }}
            >
                Analisi approfondita
                <ChevronDown className="size-4 ml-1" />
            </Button>

            {open && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => {
                            setOpen(false);
                            setCustomOpen(false);
                        }}
                    />
                    <div className="absolute right-0 mt-2 w-72 rounded-lg border border-secondary bg-primary shadow-lg z-50 overflow-hidden">
                        <div className="py-1">
                            {PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => runPrompt(preset.question)}
                                    className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-secondary transition-colors"
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        <div className="border-t border-secondary">
                            {!customOpen ? (
                                <button
                                    type="button"
                                    onClick={() => setCustomOpen(true)}
                                    className="w-full text-left px-3 py-2 text-sm text-brand hover:bg-secondary transition-colors"
                                >
                                    Domanda personalizzata…
                                </button>
                            ) : (
                                <div className="p-3 space-y-2">
                                    <textarea
                                        autoFocus
                                        rows={3}
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        placeholder="Es. Ho ricevuto un'email dal carrier che dice ETA 15 maggio, è corretto?"
                                        className="w-full text-sm rounded-md border border-secondary bg-primary p-2 text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand"
                                    />
                                    <div className="flex items-center justify-end gap-2">
                                        <Button
                                            size="sm"
                                            color="tertiary"
                                            onClick={() => {
                                                setCustomOpen(false);
                                                setCustomText("");
                                            }}
                                        >
                                            Annulla
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            onClick={handleCustom}
                                            isDisabled={customText.trim().length < 5}
                                        >
                                            Analizza
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
